import { Flex } from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { StripePayment } from "./StripePayment";
import { Bundle } from "../../../types";
import { FunnelComponentProps } from "../../funnel/types";
import { useBundlePurchase } from "../../slideInMenu/bundleDetails/hooks/useBundlePurchase";
import { useUserData } from "../../../hooks/useUserData";
import { useRefetchAccountAndRelease } from "../../../hooks/useRefetchAccountAndRelease";
import { PurchaseButton } from "./PurchaseButton";
import { useQueryClient } from "@tanstack/react-query";

export interface PurchaseBundleModalFooterProps extends FunnelComponentProps {
  bundle?: Bundle;
}

export function PurchaseBundleModalFooter({
  bundle,
  onClose,
  onSuccess: onSuccessProp,
}: PurchaseBundleModalFooterProps) {
  if (!bundle) return null;
  const { userData } = useUserData();
  const [responseError, setResponseError] = useState<string | null>(null);

  const refetchQueries = useRefetchAccountAndRelease({
    invalidateUser: false,
    extraKeys: [["bundleDetail", bundle.uuid]],
  });

  const queryClient = useQueryClient();

  // Determine pricing
  const hasGoldAccess = !!userData?.gold;
  const shouldUseGoldPrice = hasGoldAccess;
  const isGoldPricePoint = shouldUseGoldPrice;

  const onSuccess = async () => {
    const data = await refetchQueries();

    // Update bundle cache directly with purchase data
    const bundleCacheKey = `bundle-${bundle.id}`;
    queryClient.setQueryData([bundleCacheKey], (bundleData: Bundle) => {
      if (!bundleData) return bundleData;
      return { ...bundleData, user_owns_all_items: true };
    });

    if (onSuccessProp) {
      onSuccessProp({ data: [data[0], data[1]] });
    } else {
      onClose?.();
    }
  };

  const onError = (error: string) => {
    setResponseError(error);
    return error;
  };

  const bundlePurchase = useBundlePurchase({
    bundle,
    isGold: isGoldPricePoint,
    onSuccess,
    onError,
  });

  return (
    <Flex flexDirection="column" width="100%" gap="24px">
      {bundle.user_owns_all_items ? (
        <PurchaseButton
          text="View Bundle"
          onClick={() => onSuccessProp?.({})}
        />
      ) : (
        <StripePayment
          waiting={bundlePurchase.waiting}
          completePurchase={bundlePurchase.mutate}
          submitButtonText={`${t("components.payments.bundles.buyBundleNow")}`}
          responseError={responseError || bundlePurchase.cardValidationError}
          failedCard={bundlePurchase.failedCard}
          paddingBottom="16px"
        />
      )}
    </Flex>
  );
}
